import { useCallback, useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useAuthOptional } from "@/contexts/AuthContext";
import { getOpsSettings, patchOpsSettings } from "@/lib/opsPaperworkClient";
import { coerceHasSalesFloor } from "@/lib/fulfillment/salesFloorCapability";
import { coerceServicesEnabled } from "@/lib/service/serviceOperationalCapability";
import { coercePersonalAssistantEnabled } from "@/lib/personalAssistant/personalAssistantCapability";
import { BLANK_ORG_SETTINGS, applyOrgBrandCssVars, type OrgSettings } from "./orgSettingsSectionShared";
import { syncOrgLogoToAssetLibrary } from "@/lib/document/docAssetsClient";

async function trySyncLogoToAssetLibrary(logoUrl: string): Promise<string | null> {
  const trimmed = logoUrl.trim();
  if (!trimmed) return null;
  const r = await syncOrgLogoToAssetLibrary(trimmed);
  if (!r.ok) return r.error;
  return r.created
    ? "Logo added to Documents → Asset library."
    : "Logo updated in Documents → Asset library.";
}

export function useOrgSettingsSection(sb: SupabaseClient, organizationId: string) {
  const [settings, setSettings] = useState<OrgSettings>(BLANK_ORG_SETTINGS);
  const [original, setOriginal] = useState<OrgSettings>(BLANK_ORG_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [confirmPending, setConfirmPending] = useState(false);
  const [kbTestingModeEnabled, setKbTestingModeEnabled] = useState(false);
  // Fulfillment Slice 3 capability flag (organizations.has_sales_floor).
  // Saved via the dedicated `admin_set_has_sales_floor` RPC, not via
  // `admin_update_org_settings` — keeps the capability bit isolated
  // from the branding-save flow.
  const [hasSalesFloor, setHasSalesFloor] = useState(false);
  const [savingHasSalesFloor, setSavingHasSalesFloor] = useState(false);
  const [servicesEnabled, setServicesEnabled] = useState(false);
  const [savingServicesEnabled, setSavingServicesEnabled] = useState(false);
  const [personalAssistantEnabled, setPersonalAssistantEnabled] = useState(false);
  const [savingPersonalAssistantEnabled, setSavingPersonalAssistantEnabled] = useState(false);
  const auth = useAuthOptional();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await sb
        .from("organizations")
        .select(
          "name, brand_name, logo_url, app_display_name, color_primary, color_secondary, tagline, has_sales_floor, services_enabled, personal_assistant_enabled"
        )
        .eq("id", organizationId)
        .maybeSingle();
      if (error) throw error;
      const orgName = ((data as any)?.name ?? "").trim();
      const loaded: OrgSettings = {
        organization_name: orgName,
        brand_name: ((data as any)?.brand_name ?? "").trim() || orgName,
        logo_url: (data as any)?.logo_url ?? "",
        app_display_name: (data as any)?.app_display_name ?? "",
        color_primary: (data as any)?.color_primary ?? "",
        color_secondary: (data as any)?.color_secondary ?? "",
        tagline: (data as any)?.tagline ?? "",
      };
      setSettings(loaded);
      setOriginal(loaded);
      setHasSalesFloor(coerceHasSalesFloor((data as any)?.has_sales_floor));
      setServicesEnabled(coerceServicesEnabled((data as any)?.services_enabled));
      setPersonalAssistantEnabled(
        coercePersonalAssistantEnabled((data as any)?.personal_assistant_enabled),
      );
      applyOrgBrandCssVars(loaded);
    } catch (e: any) {
      setMsg({ text: e?.message || String(e), ok: false });
    } finally {
      setLoading(false);
    }
  }, [organizationId, sb]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void (async () => {
      const res = await getOpsSettings();
      if (!res.ok || !res.data?.settings) return;
      const settingsRow = res.data.settings as Record<string, unknown>;
      const kbTestingMode = settingsRow.kb_testing_mode_enabled === true;
      setKbTestingModeEnabled(kbTestingMode);
    })();
  }, [organizationId]);

  const hasBrandingChange =
    settings.brand_name !== original.brand_name ||
    settings.app_display_name !== original.app_display_name ||
    settings.color_primary !== original.color_primary ||
    settings.color_secondary !== original.color_secondary;

  const isDirty =
    settings.brand_name !== original.brand_name ||
    settings.logo_url !== original.logo_url ||
    settings.app_display_name !== original.app_display_name ||
    settings.color_primary !== original.color_primary ||
    settings.color_secondary !== original.color_secondary ||
    settings.tagline !== original.tagline;

  async function syncFromGhl() {
    setSyncing(true);
    setMsg(null);
    try {
      const session = auth?.session;
      if (!session?.access_token) throw new Error("Not authenticated");
      const res = await fetch(
        `/api/ghl/pipelines?action=branding&organizationId=${encodeURIComponent(organizationId)}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `GHL error ${res.status}`);
      const logo: string | null = json.logo ?? null;
      const name: string | null = json.name ?? null;
      const primaryColor: string | null = json.primaryColor ?? null;
      const buttonColor: string | null = json.buttonColor ?? null;
      setSettings((s) => ({
        ...s,
        ...(logo ? { logo_url: logo } : {}),
        ...(name && !s.brand_name ? { brand_name: name } : {}),
        ...(primaryColor ? { color_primary: primaryColor } : {}),
        ...(buttonColor ? { color_secondary: buttonColor } : {}),
      }));
      applyOrgBrandCssVars({
        ...(primaryColor ? { color_primary: primaryColor } : {}),
        ...(buttonColor ? { color_secondary: buttonColor } : {}),
      });
      const synced: string[] = [];
      if (logo) synced.push("logo");
      if (primaryColor) synced.push("primary color");
      if (buttonColor) synced.push("secondary color");
      let assetNote: string | null = null;
      if (logo) {
        assetNote = await trySyncLogoToAssetLibrary(logo);
      }
      setMsg({
        text:
          [
            synced.length > 0
              ? `Synced from GHL: ${synced.join(", ")}. Colors must be copied manually from the GHL brand palette for now.`
              : "No logo data found in this GHL location. Colors must be copied manually from the GHL brand palette for now.",
            assetNote,
          ]
            .filter(Boolean)
            .join(" "),
        ok: true,
      });
    } catch (e: any) {
      setMsg({ text: e?.message || String(e), ok: false });
    } finally {
      setSyncing(false);
    }
  }

  async function save(): Promise<{ ok: true } | { ok: false; error: string }> {
    setLoading(true);
    setMsg(null);
    try {
      const { error } = await sb.rpc("admin_update_org_settings", {
        p_org_id: organizationId,
        p_brand_name: settings.brand_name || null,
        p_logo_url: settings.logo_url || null,
        p_app_display_name: settings.app_display_name || null,
        p_color_primary: settings.color_primary || null,
        p_color_secondary: settings.color_secondary || null,
        p_tagline: settings.tagline,
        p_product_types: [],
      });
      if (error) throw error;
      let assetNote: string | null = null;
      if (settings.logo_url.trim() && settings.logo_url !== original.logo_url) {
        assetNote = await trySyncLogoToAssetLibrary(settings.logo_url);
      }
      setMsg({
        text: ["Settings saved.", assetNote].filter(Boolean).join(" "),
        ok: true,
      });
      setOriginal(settings);
      applyOrgBrandCssVars(settings);
      window.dispatchEvent(new CustomEvent("org-settings-saved"));
      return { ok: true };
    } catch (e: any) {
      const message = e?.message || String(e);
      setMsg({ text: message, ok: false });
      return { ok: false, error: message };
    } finally {
      setLoading(false);
    }
  }

  async function saveKbTestingMode() {
    const res = await patchOpsSettings({
      kb_testing_mode_enabled: kbTestingModeEnabled,
    });
    if (!res.ok) {
      setMsg({ text: `KB testing mode save failed: ${res.error}`, ok: false });
      return;
    }
    setMsg({
      text: kbTestingModeEnabled
        ? "KB testing mode enabled for this organization."
        : "KB testing mode disabled for this organization.",
      ok: true,
    });
  }

  async function saveHasSalesFloor() {
    setSavingHasSalesFloor(true);
    setMsg(null);
    try {
      const { data, error } = await sb.rpc("admin_set_has_sales_floor", {
        p_org_id: organizationId,
        p_value: hasSalesFloor,
      });
      if (error) throw error;
      const persisted = coerceHasSalesFloor(data);
      setHasSalesFloor(persisted);
      setMsg({
        text: persisted
          ? "Sales floor enabled — Fulfillment bucket is visible to this org's users on next refresh."
          : "Sales floor disabled — Fulfillment bucket is hidden from this org's users on next refresh.",
        ok: true,
      });
    } catch (e: any) {
      setMsg({ text: e?.message || String(e), ok: false });
    } finally {
      setSavingHasSalesFloor(false);
    }
  }

  async function saveServicesEnabled() {
    setSavingServicesEnabled(true);
    setMsg(null);
    try {
      const { data, error } = await sb.rpc("admin_set_services_enabled", {
        p_org_id: organizationId,
        p_value: servicesEnabled,
      });
      if (error) throw error;
      const persisted = coerceServicesEnabled(data);
      setServicesEnabled(persisted);
      setMsg({
        text: persisted
          ? "Service department enabled — operational Service bucket is visible on next refresh."
          : "Service department disabled — operational Service bucket is hidden on next refresh.",
        ok: true,
      });
    } catch (e: any) {
      setMsg({ text: e?.message || String(e), ok: false });
    } finally {
      setSavingServicesEnabled(false);
    }
  }

  async function savePersonalAssistantEnabled() {
    setSavingPersonalAssistantEnabled(true);
    setMsg(null);
    try {
      const { data, error } = await sb.rpc("admin_set_personal_assistant_enabled", {
        p_org_id: organizationId,
        p_value: personalAssistantEnabled,
      });
      if (error) throw error;
      const persisted = coercePersonalAssistantEnabled(data);
      setPersonalAssistantEnabled(persisted);
      setMsg({
        text: persisted
          ? "Personal Assistant enabled — eligible users can use assistant mode after refresh."
          : "Personal Assistant disabled — assistant mode is unavailable on next refresh.",
        ok: true,
      });
    } catch (e: any) {
      setMsg({ text: e?.message || String(e), ok: false });
    } finally {
      setSavingPersonalAssistantEnabled(false);
    }
  }

  return {
    settings,
    setSettings,
    original,
    setOriginal,
    loading,
    syncing,
    msg,
    setMsg,
    confirmPending,
    setConfirmPending,
    kbTestingModeEnabled,
    setKbTestingModeEnabled,
    hasSalesFloor,
    setHasSalesFloor,
    savingHasSalesFloor,
    servicesEnabled,
    setServicesEnabled,
    savingServicesEnabled,
    personalAssistantEnabled,
    setPersonalAssistantEnabled,
    savingPersonalAssistantEnabled,
    hasBrandingChange,
    isDirty,
    syncFromGhl,
    save,
    saveKbTestingMode,
    saveHasSalesFloor,
    saveServicesEnabled,
    savePersonalAssistantEnabled,
  };
}
